import React, {useEffect, useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config.client'

import {
    Box,
    Flex,
    Heading,
    Image,
    Link,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Skeleton, SkeletonText} from '@chakra-ui/react'

import withRegistration from '../../../components/with-registration'
import useTagManager from '../../../hooks/use-tag-manager'
import {getUrl} from '../../../utils/cloudinary-utils'
import {KS_SIGNUP_URL} from '../../../constants'
import useContentAsset from '../../../hooks/use-content-asset.js'
import {AuthModal, useAuthModal} from '../../../hooks/use-auth-modal'

const LinkWithRegistration = withRegistration(Link)

const WishlistGuestPromptSkeleton = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')

    return (
        <Box {...cartStyles.wishlistInCart.callout} data-testid="sf-cart-wishlist-guest-skeleton">
            <Flex {...cartStyles.wishlistInCart.titleWithIcon}>
                <Skeleton borderRadius="full" height="16px" width="16px" />
                <Skeleton height="36px" width="190px" />
            </Flex>

            <SkeletonText noOfLines={1} spacing={3} skeletonHeight="14px" width="100%" />
        </Box>
    )
}

export const WishlistGuestPrompt = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const tagManager = useTagManager()
    const authModal = useAuthModal()
    const assetContentRef = useRef(null)
    const {
        app: {commerceAPI}
    } = getConfig()

    const {hostUrl} = commerceAPI.storefront
    const {data: wishlistGuestMsg, isLoading: wishlistGuestMsgLoading} = useContentAsset(
        'wishlist-guest-callout-msg'
    )

    useEffect(() => {
        if (!wishlistGuestMsg?.contentAssetHTML || !assetContentRef.current) return

        const signInCTA = assetContentRef.current.querySelector('.wishlist-signin-cta')
        if (!signInCTA) return

        const handleClick = (event) => {
            event.preventDefault()
            authModal.onOpen()
        }

        signInCTA.addEventListener('click', handleClick)

        return () => signInCTA.removeEventListener('click', handleClick)
    }, [wishlistGuestMsg?.contentAssetHTML, authModal])

    if (wishlistGuestMsgLoading) {
        return <WishlistGuestPromptSkeleton />
    }

    return (
        <>
            <Box {...cartStyles.wishlistInCart.callout} data-testid="sf-cart-wishlist-guest">
                {/* Header */}
                {wishlistGuestMsg?.contentAssetHTML ? (
                    <Box
                        ref={assetContentRef}
                        dangerouslySetInnerHTML={{__html: wishlistGuestMsg.contentAssetHTML}}
                        {...cartStyles.wishlistInCart.emptyStateAsset}
                    />
                ) : (
                    <>
                        <Flex {...cartStyles.wishlistInCart.titleWithIcon}>
                            <Image
                                src={getUrl('UIImages/images/empty-heart-icon.svg')}
                                width="16px"
                                height="16px"
                                alt="empty heart"
                            />

                            <Heading {...cartStyles.wishlistInCart.title}>
                                <FormattedMessage
                                    defaultMessage="Your Wishlist"
                                    id="cart.your.wishlist.title"
                                />
                            </Heading>
                        </Flex>

                        {/* Message */}
                        <Text {...cartStyles.wishlistInCart.bodyText}>
                            {/* Sign In (opens AuthModal via withRegistration) */}
                            <LinkWithRegistration
                                {...cartStyles.wishlistInCart.inlineLink}
                                isCart={true}
                            >
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="cart.wishlist.guest.sign_in"
                                />
                            </LinkWithRegistration>{' '}
                            <FormattedMessage defaultMessage="or" id="cart.wishlist.guest.or" />{' '}
                            {/* Create Account */}
                            <Link
                                variant="link"
                                {...cartStyles.wishlistInCart.inlineLink}
                                href={hostUrl + KS_SIGNUP_URL}
                                onClick={() =>
                                    tagManager.formFieldChange('login-form', 'Create Account')
                                }
                            >
                                <FormattedMessage
                                    defaultMessage="Create an Account"
                                    id="create.an.account"
                                />
                            </Link>{' '}
                            <FormattedMessage
                                defaultMessage="to enjoy exclusive perks and see what you have previously saved."
                                id="cart.wishlist.guest.message"
                            />
                        </Text>
                    </>
                )}
            </Box>
            <AuthModal {...authModal} />
        </>
    )
}
